import { useEffect } from "react";
import { Formik, useField } from "formik";
import { Spinner } from "react-bootstrap";

import {
  Field,
  Input,
  Label,
  SubmitButton,
  Form,
} from "../../../../../components/forms";
import Panel from "../../Panel";
import useEmailScreen from "./useEmailScreen";
import { Heading, Intro } from "../../Typography";
import { Logo } from "../../Logo";

const EmailField = ({
  emailExists,
  setEmail,
  isCheckingEmailExists,
}: Pick<
  ReturnType<typeof useEmailScreen>,
  "emailExists" | "setEmail" | "isCheckingEmailExists"
>) => {
  const [field, meta] = useField("email");

  useEffect(() => {
    field.value && !meta.error ? setEmail(field.value) : setEmail("");
  }, [field.value, meta.error, meta.touched, setEmail]);

  return (
    <Field name="email">
      <Label>Email address</Label>
      <Input type="email" />
      {emailExists !== undefined || isCheckingEmailExists ? (
        <div className="mt-1">
          {!isCheckingEmailExists ? (
            <div className="text-success small">
              ✅{" "}
              {emailExists
                ? "We found you in our system"
                : "Looks good, we'll get you registered"}
            </div>
          ) : (
            <Spinner animation="border" size="sm" />
          )}
        </div>
      ) : null}
    </Field>
  );
};

type Props = {
  loading?: boolean;
  introText?: string;
  onNext: ({
    email,
    emailExists,
  }: {
    email: string;
    emailExists: boolean;
  }) => void;
};

export default function EmailScreen({ onNext, introText, loading }: Props) {
  const {
    initialValues,
    onSubmit,
    schema,
    setEmail,
    isCheckingEmailExists,
    emailExists,
  } = useEmailScreen({ onNext });

  if (!introText) {
    introText =
      "Enter your email, we need this to uniquely identify you and notify you of results.";
  }

  return (
    <Panel>
      <Logo />
      <Heading>Griptonite account</Heading>
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : null}
      <Intro>{introText}</Intro>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={schema}
        validateOnChange
      >
        <Form>
          <EmailField
            setEmail={setEmail}
            emailExists={emailExists}
            isCheckingEmailExists={isCheckingEmailExists}
          />
          <SubmitButton>Next</SubmitButton>
        </Form>
      </Formik>
    </Panel>
  );
}
